<script setup lang="ts">
import { useQueryManager } from "@/stores/query-manager";
import { SoftDateTimePicker } from "@soft-db/ui";
import { DateTime } from "luxon";
import { useCreateSiteArchive } from "../../api/site-query";
import AppDialog from "../common/AppDialog.vue";

const props = defineProps<{
	siteId: string;
}>();

const openModel = defineModel<boolean>("open");

const { t } = useI18n();

const initialDateTime = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

const range = ref<[DateTime, DateTime]>([initialDateTime, initialDateTime]);

const createTaskMutation = useCreateSiteArchive();

const queryManager = useQueryManager();

const creatingTask = ref(false);
async function createTasks() {
	creatingTask.value = true;
	const [startTime, endTime] = range.value;
	const numberOfDays = Math.floor(endTime.diff(startTime).as("day"));

	let taskStart = startTime;

	await queryManager.chainInvalidation(async () => {
		for (let i = 0; i <= numberOfDays; i++) {
			const taskEnd = taskStart.plus({ day: 1 });

			await createTaskMutation.mutateAsync({
				id: props.siteId,
				startTime: taskStart.toJSDate(),
				endTime: taskEnd.toJSDate(),
			});

			taskStart = taskEnd;
		}
	});

	creatingTask.value = false;
	openModel.value = false;

	range.value = [initialDateTime, initialDateTime];
}
</script>

<template>
	<AppDialog
		v-model="openModel"
		class="w-125"
		:actionText="t('archive.createTasksButton')"
		:actionDisabled="creatingTask"
		:actionLoading="creatingTask"
		:onClick:action="createTasks"
	>
		<template #title>
			{{ t("archive.createTask") }}
		</template>
		<template #content>
			<div class="flex w-full flex-col">
				<SoftDateTimePicker
					v-model="range"
					:label="t('common.range')"
					disableTime
					range
					:format="DateTime.DATE_FULL"
					:max="initialDateTime"
				/>
			</div>
		</template>
	</AppDialog>
</template>
